// import { TokenConfig } from '../../types'; // TokenConfig is defined locally


// Re-defining TokenConfig here if it's not in global types, based on centralizedTokenSystem.ts
// It seems centralizedTokenSystem.ts defines it. I should probably move it to a shared type file or keep it here.
// For now, I will define it here as it was in centralizedTokenSystem.ts

export interface TokenConfig {
    // Base rates (tokens per operation)
    baseRates: {
        chatMessage: number;
        chatMessageWithAttachment: number;
        quizGeneration: number;
        examGeneration: number;
        fileAnalysis: number;
    };

    // Multipliers based on complexity
    complexityMultipliers: {
        simple: number;      // Basic questions
        moderate: number;    // Standard questions
        complex: number;     // Multi-step, calculations
        advanced: number;    // Professional-level analysis
    };

    // Model-based multipliers
    modelMultipliers: {
        flash: number;       // Gemini 2.0 Flash
        pro: number;         // Gemini 1.5 Pro
    };

    // Attachment-based charges
    attachmentRates: {
        perKB: number;           // Tokens per KB of attachment
        imageBase: number;       // Base cost for image
        pdfBase: number;         // Base cost for PDF
        preAnalysisBonus: number; // Extra for pre-analysis
    };

    // Feature-based charges
    featureRates: {
        whiteboardGeneration: number;
        thoughtProcess: number;
        multipleAttachments: number; // Extra per additional attachment
        followUpContext: number;     // Cost for using attachment context
    };

    // Premium user discounts
    premiumDiscounts: {
        chatDiscount: number;        // % discount on chat (0-100)
        quizDiscount: number;        // % discount on quiz
        examDiscount: number;        // % discount on exam
        attachmentDiscount: number;  // % discount on attachments
    };

    // Daily limits (MUST match constants.ts values)
    limits: {
        standardUserDaily: number;  // Standard/Free tier - matches constants.freeTokenLimit
        premiumUserDaily: number;   // Premium tier - matches constants.premiumTokenLimit
        maxUserDaily: number;       // MAX tier - matches constants.maxTokenLimit
        maxPerRequest: number;
    };

    // Refund settings
    refunds: {
        enabled: boolean;
        errorRefundPercent: number;   // % to refund on error (0-100)
        partialResponsePercent: number; // % to charge for partial response
    };
}

export const DEFAULT_TOKEN_CONFIG: TokenConfig = {
    baseRates: {
        // Sustainable pricing - balances user experience with profitability
        chatMessage: 25,              // Base chat: 200 msgs/day free tier
        chatMessageWithAttachment: 60, // With files: ~83 msgs/day
        quizGeneration: 120,          // Per quiz (~10 questions): ~41 quizzes
        examGeneration: 250,          // Per exam (~50 questions): ~20 exams
        fileAnalysis: 40,             // File analysis alone: ~125 analyses
    },
    complexityMultipliers: {
        simple: 0.8,                  // Quick answers: 20 tokens
        moderate: 1.0,                // Standard: 25 tokens
        complex: 1.8,                 // Detailed: 45 tokens
        advanced: 3.5,                // Expert: 88 tokens
    },
    modelMultipliers: {
        flash: 1.0,                   // Standard model
        pro: 1.5,                     // Pro model costs more
    },
    attachmentRates: {
        perKB: 0.05,                  // Per KB of attachment
        imageBase: 30,                // Base image analysis
        pdfBase: 40,                  // Base PDF analysis
        preAnalysisBonus: 15,         // Pre-analysis intelligence cost
    },
    featureRates: {
        whiteboardGeneration: 20,     // Whiteboard/chart generation
        thoughtProcess: 10,           // Extended thinking
        multipleAttachments: 25,      // Per additional attachment
        followUpContext: 8,           // Context from previous attachments
    },
    // NO DISCOUNTS - Tiers get MORE tokens, not cheaper rates
    premiumDiscounts: {
        chatDiscount: 0,              // No discount (more tokens instead)
        quizDiscount: 0,              // No discount
        examDiscount: 0,              // No discount
        attachmentDiscount: 0,        // No discount
    },
    limits: {
        standardUserDaily: 2000,     // 2K tokens/day for Standard (~40 messages)
        premiumUserDaily: 15000,     // 15K tokens/day for Premium (~300 messages)
        maxUserDaily: 50000,         // 50K tokens/day for MAX (~1000 messages)
        maxPerRequest: 500,          // 500 max per single request
    },
    refunds: {
        enabled: true,
        errorRefundPercent: 100,
        partialResponsePercent: 50,
    },
};

// State to hold current config
let currentConfig: TokenConfig = { ...DEFAULT_TOKEN_CONFIG };
const configListeners: Set<(config: TokenConfig) => void> = new Set();

/**
 * Load token configuration from localStorage
 */
export const loadTokenConfig = (): TokenConfig => {
    try {
        const stored = localStorage.getItem('token_config');
        if (stored) {
            const parsed = JSON.parse(stored);
            currentConfig = { ...DEFAULT_TOKEN_CONFIG, ...parsed };
            return currentConfig;
        }
    } catch (error) {
        console.error('[TokenSystem] Failed to load config:', error);
    }
    return currentConfig;
};

/**
 * Save token configuration
 */
export const saveTokenConfig = (config: Partial<TokenConfig>): void => {
    currentConfig = { ...currentConfig, ...config };
    localStorage.setItem('token_config', JSON.stringify(currentConfig));
    console.log('[TokenSystem] Configuration saved');

    // Notify listeners
    configListeners.forEach(listener => listener(currentConfig));
};

/**
 * Get current configuration
 */
export const getTokenConfig = (): TokenConfig => {
    return { ...currentConfig };
};

/**
 * Subscribe to configuration changes
 */
export const onConfigChange = (callback: (config: TokenConfig) => void): () => void => {
    configListeners.add(callback);
    return () => configListeners.delete(callback);
};

// Initialize config on load
loadTokenConfig();
